import React from "react";
import { ChevronsUpDown, SearchIcon } from "lucide-react";
import { Button } from "components/Graphs/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Input } from "components/Graphs/Input";
import { Checkbox } from "components/Graphs/Checkbox";
import { Label } from "recharts";

interface Item {
  id: string;
  label: string;
}

interface SelectionPopoverProps {
  items: Item[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

export const SelectionPopover: React.FC<SelectionPopoverProps> = ({
  items,
  selectedValues,
  onSelectionChange,
  placeholder = "Select Launchables...",
  label = "Select Launchables",
  className = "bg-primaryBg",
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleSelectItem = (itemId: string) => {
    const newSelectedValues = selectedValues.includes(itemId)
      ? selectedValues.filter((id) => id !== itemId)
      : [...selectedValues, itemId];

    onSelectionChange(newSelectedValues);
  };

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTriggerLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const selectedItem = items.find((item) =>
        selectedValues.includes(item.id)
      );
      return selectedItem ? selectedItem.label : "1 Launchable selected";
    }
    return `${selectedValues.length} Launchables selected`;
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className={`${className} justify-between bg-primaryBg truncate font-normal`}
        >
          {getTriggerLabel()}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-primaryBg">
        <div className="p-3 border-b border-zinc-800">
          <div className="relative">
            <Input
              type="search"
              placeholder="Search Launchables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
        <div className="h-[200px] overflow-y-auto">
          <div className="p-3">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center space-x-2 py-2 px-1 hover:bg-accent rounded-md"
                >
                  <Checkbox
                    id={`item-${item.id}`}
                    checked={selectedValues.includes(item.id)}
                    onCheckedChange={() => handleSelectItem(item.id)}
                    aria-labelledby={`label-${item.id}`}
                  />
                  <label
                    htmlFor={`item-${item.id}`}
                    id={`label-${item.id}`}
                    className="font-normal flex-1 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSelectItem(item.id);
                    }}
                  >
                    {item.label}
                  </label>
                </div>
              ))
            ) : (
              <p className="text-sm text-secondary text-center py-4">
                No Launchables found.
              </p>
            )}
          </div>
        </div>
        {selectedValues.length > 0 && (
          <div className="p-2 border-t border-zinc-800">
            <Button
              variant="ghost"
              className="w-full text-sm h-4 p-4"
              onClick={handleClearAll}
            >
              Clear selections ({selectedValues.length})
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
